import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { DropDownOptions } from 'devextreme-react/autocomplete';
import Form, { Item, RequiredRule } from 'devextreme-react/form';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator from 'devextreme-react/validator';

import { AppCol, AppFieldBox, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { Button, DateBox, NumberBox, SelectBox, TextArea } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';

import {
  useChangeCashFlowIncomeSellingMutation,
  useCreateCashFlowIncomeSellingMutation,
  useLazyGetCashFlowIncomeSellingQuery,
} from 'bus/cash';

import { ChangeCashFlowIncomeSellingDetail } from './components';
import { initValue } from './utils';

const ChangeCashFlowIncomeSelling: FC = () => {
  const history = useHistory();
  let { id } = useParams<{ id: string }>();

  const [form, setForm] = useState(initValue);
  const createValidationRef = useRef<ValidationGroup>(null);

  const [lazyGetCashFlowIncomeSelling] = useLazyGetCashFlowIncomeSellingQuery();
  const [changeCashFlowIncomeSelling, { isLoading: isLoadingChange }] = useChangeCashFlowIncomeSellingMutation();
  const [createCashFlowIncomeSelling, { isLoading: isLoadingCreate }] = useCreateCashFlowIncomeSellingMutation();

  const disabledSubmit = isLoadingChange || isLoadingCreate;

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();

    if (createValidationRef.current?.instance.validate().isValid) {
      if (id) {
        await changeCashFlowIncomeSelling({ id, ...form });
        toast.success('Изменения успешно сохранены', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        await createCashFlowIncomeSelling(form);
        history.push('/cash-flow');
      }
    }
  };

  const requestDateSource = useMemo(() => {
    const defaultFilter = ['cashFlowType', '=', '0'];
    return $createDataSource('/v1/CashFlowItem', 'id', { defaultFilter });
  }, []);

  const getInit = useCallback(async () => {
    if (id) {
      const result = await lazyGetCashFlowIncomeSelling({ id });
      result && setForm(result.data!);
    }
  }, [id, lazyGetCashFlowIncomeSelling]);

  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className="mt-5">
      <div style={{ width: 750 }}>
        <form onSubmit={handleSubmit}>
          <ValidationGroup ref={createValidationRef}>
            <AppLayoutTop title="Приход от продажи">
              <Button
                text="Сохранить"
                type="default"
                useSubmitBehavior={false}
                onClick={handleSubmit}
                disabled={disabledSubmit}
              />
            </AppLayoutTop>
            <div className="dx-card">
              <Form formData={form}>
                <Item colSpan={12} colCount={12} itemType="group">
                  <AppCol col={4}>
                    <AppFieldBox label="Дата" labelTop>
                      <DateBox
                        stylingMode="underlined"
                        type="date"
                        onValueChange={(e) => setForm((prev) => ({ ...prev, date: e as string }))}
                        useMaskBehavior
                        dateSerializationFormat="yyyy-MM-dd"
                        value={form.date}
                      >
                        <Validator>
                          <RequiredRule message="Заполните поле Дата" />
                        </Validator>
                      </DateBox>
                    </AppFieldBox>
                  </AppCol>
                  <AppCol col={4}>
                    <AppFieldBox label="Статья движения ДС" labelTop>
                      <SelectBox
                        stylingMode="underlined"
                        dataSource={requestDateSource}
                        displayExpr="name"
                        valueExpr="id"
                        value={form.cashFlowItemId}
                        searchEnabled
                        onItemClick={(e) => {
                          setForm((prev) => ({ ...prev, cashFlowItemId: e.itemData.id }));
                        }}
                      >
                        <DropDownOptions
                          title="Выберите Статья движения ДС"
                          closeOnOutsideClick={true}
                          showTitle={false}
                        />
                        <Validator>
                          <RequiredRule message="Выберите Статья движения ДС" />
                        </Validator>
                      </SelectBox>
                    </AppFieldBox>
                  </AppCol>

                  {id && (
                    <>
                      <AppCol col={4}>
                        <AppFieldBox label="Сумма" labelTop>
                          <NumberBox
                            stylingMode="underlined"
                            placeholder="0"
                            format={MONEY_FORMAT}
                            value={form.receivedSum}
                            readOnly
                          ></NumberBox>
                        </AppFieldBox>
                      </AppCol>

                      <AppCol col={4}>
                        <AppFieldBox label="Остаток" labelTop>
                          <NumberBox
                            stylingMode="underlined"
                            placeholder="0"
                            format={MONEY_FORMAT}
                            value={form.remainderSum}
                            readOnly
                          ></NumberBox>
                        </AppFieldBox>
                      </AppCol>
                    </>
                  )}

                  <AppCol col={12}>
                    <AppFieldBox label="Комментарий" labelTop>
                      <TextArea
                        stylingMode="underlined"
                        placeholder="Введите комментарий"
                        mode="text"
                        value={form.comment || ''}
                        onChange={(e: any) => {
                          setForm((prev) => ({ ...prev, comment: e.event.currentTarget.value }));
                        }}
                      />
                    </AppFieldBox>
                  </AppCol>
                </Item>
              </Form>
            </div>
          </ValidationGroup>
        </form>
      </div>
      <ClaimsProvider claimsBlock={['CanLookIncomeSellingDetails']}>
        {id && <ChangeCashFlowIncomeSellingDetail cashFlowId={id} refreshForm={getInit} />}
      </ClaimsProvider>
    </div>
  );
};

export default ChangeCashFlowIncomeSelling;
